import { inject } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { partnerId } from '../../globals';
import { toObservable } from '@angular/core/rxjs-interop';
import { SelectTemplateComponent } from '../emails/templates/select-template/select-template.component';
// todo: remove when we move tokens to a seperate lib to preserve lazyloading
// eslint-disable-next-line @nx/enforce-module-boundaries
import {
  PREVIEW_DATA_TYPE,
  QuotaConfig,
  SenderTypeFromBusinessId,
  stepDataForBusinessEmail,
} from '@galaxy/campaign/dependencies';
import { catchError, map, shareReplay, switchMap } from 'rxjs/operators';
import { AccountGroupService } from '../account-group';
import { AuthService } from '../auth.service';
import { Environment, EnvironmentService } from '@galaxy/core';
import { CONFIG_TYPE, ValidationErrorConfig } from '@galaxy/campaign/shared';
import { combineLatest, Observable, of } from 'rxjs';
import { PartnerMarketConfigService } from '../partner-market-config';
import { CampaignConfigurationService } from './campaign-configuration.service';
import { OwnerType, SmsApiService } from '@vendasta/smsv2';
import { ValidationErrorCode } from '@vendasta/campaigns';

export const CampaignConfigFactory = () => {
  const campaignsProdAppID = 'MP-ZGJ6V4QRP77WPMDKXS6VDRNX58Q42P7P';
  const campaignsDemoAppID = 'MP-WF5KS7FHTGV4LR5DBQ4D6XKCCDB5B6G7';
  const ag = inject(AccountGroupService) as AccountGroupService;
  const authService = inject(AuthService);
  const environment = inject(EnvironmentService);
  const partnerMarketConfigService = inject(PartnerMarketConfigService);
  const campaignConfigurationService = inject(CampaignConfigurationService);
  const smsService = inject(SmsApiService);
  const dialog = inject(MatDialog);
  const cfg: CONFIG_TYPE = {
    sender$: SenderTypeFromBusinessId(ag.currentAccountGroupId$),
    userId$: toObservable(authService.userId),
    basePath$: ag.currentAccountGroupId$.pipe(map((v) => `account/location/${v}/campaigns`)),
    validationErrorConfig$: campaignValidationErrorConfig(ag.currentAccountGroupId$),
    location$: ag.currentAccountGroup$.pipe(map((v) => v.country)),
    productBasePath$: combineLatest([ag.currentAccountGroupId$, partnerMarketConfigService.businessAppConfig$]).pipe(
      map(([accountGroupID, businessAppConfig]) => {
        if (!businessAppConfig.showStore) {
          return null;
        }
        if (environment.getEnvironment() == Environment.DEMO) {
          return `account/location/${accountGroupID}/store/app/${campaignsDemoAppID}`;
        } else if (environment.getEnvironment() == Environment.PROD) {
          return `account/location/${accountGroupID}/store/app/${campaignsProdAppID}`;
        }
      }),
    ),
    smsSettingsPath$: ag.currentAccountGroupId$.pipe(
      map((accountGroupID) => {
        return `/account/location/${accountGroupID}/settings/sms-configuration`;
      }),
    ),
    campaignsConfigurationService$: of(campaignConfigurationService).pipe(shareReplay(1)),
    enabledFilterSections: ['Status'],
    showRegulationForm$: ag.currentAccountGroupId$.pipe(
      switchMap((accountGroupId) =>
        smsService
          .getRegulationForms({
            ownerId: accountGroupId,
            ownerType: OwnerType.OWNER_TYPE_ACCOUNT_GROUP,
          })
          .pipe(
            catchError((error) => {
              console.error('Error getting regulation forms:', error);
              return of(null);
            }),
          ),
      ),
      map((response) => !!response && Object.keys(response).length > 0),
    ),
    showStandaloneEmail$: ag.currentAccountGroupId$.pipe(
      map((accountGroupId) => partnerId === 'VUNI' && accountGroupId === 'AG-856D578M48'),
    ),
    // TODO: SNP-4081 enable when standalone email flow is complete
    // showStandaloneEmail$: featureFlagService.checkFeatureFlag(partnerId, '', 'standalone_emails').pipe(
    //   catchError(() => of(false)),
    //   shareReplay({ refCount: true, bufferSize: 1 }),
    // ),
    openTemplatePicker: () =>
      dialog
        .open(SelectTemplateComponent, {
          width: '90vw',
          maxWidth: '1200px',
        })
        .afterClosed(),
  };
  return cfg;
};

export const CampaignPreviewDataFactory = () => {
  const ag = inject(AccountGroupService) as AccountGroupService;
  const cfg: PREVIEW_DATA_TYPE = ag.currentAccountGroupId$.pipe(map((agid) => stepDataForBusinessEmail(agid)));
  return cfg;
};

export const CampaignQuotaConfigFactory = () => {
  const ag = inject(AccountGroupService) as AccountGroupService;
  const cfg: QuotaConfig = {
    sender$: SenderTypeFromBusinessId(ag.currentAccountGroupId$),
    showSmsQuota: true,
  };
  return cfg;
};

function campaignValidationErrorConfig(accountGroupID$: Observable<string>) {
  return accountGroupID$.pipe(
    map((accountGroupID) => {
      const smsConfigLink = `account/location/${accountGroupID}/settings/sms-configuration`;
      const config = new Map<ValidationErrorCode, ValidationErrorConfig>([
        [
          ValidationErrorCode.VALIDATION_ERROR_CODE_A2P_REGISTRATION_MISSING,
          {
            translationKey: 'VALIDATION.SENDER_VALIDATION.ERRORS.A2P_REGISTRATION_MISSING',
            ctaLink: smsConfigLink,
          },
        ],
        [
          ValidationErrorCode.VALIDATION_ERROR_CODE_A2P_REGISTRATION_IN_PROGRESS,
          {
            translationKey: 'VALIDATION.SENDER_VALIDATION.ERRORS.A2P_REGISTRATION_IN_PROGRESS',
            ctaLink: smsConfigLink,
          },
        ],
        [
          ValidationErrorCode.VALIDATION_ERROR_CODE_A2P_REGISTRATION_REJECTED,
          {
            translationKey: 'VALIDATION.SENDER_VALIDATION.ERRORS.A2P_REGISTRATION_REJECTED',
            ctaLink: smsConfigLink,
          },
        ],
        [
          ValidationErrorCode.VALIDATION_ERROR_CODE_SMS_SUBACCOUNT_MISSING,
          {
            translationKey: 'VALIDATION.SENDER_VALIDATION.ERRORS.SMS_SUBACCOUNT_MISSING',
            ctaLink: smsConfigLink,
          },
        ],
        [
          ValidationErrorCode.VALIDATION_ERROR_CODE_SMS_UNSUPPORTED_COUNTRY,
          {
            translationKey: 'VALIDATION.SENDER_VALIDATION.ERRORS.SMS_UNSUPPORTED_COUNTRY',
          },
        ],
        [
          ValidationErrorCode.VALIDATION_ERROR_CODE_SMS_UNSUPPORTED_SENDER,
          {
            translationKey: 'VALIDATION.SENDER_VALIDATION.ERRORS.SMS_UNSUPPORTED_SENDER',
          },
        ],
        [
          ValidationErrorCode.VALIDATION_ERROR_CODE_REGULATION_REQUIRED,
          {
            translationKey: 'VALIDATION.SENDER_VALIDATION.ERRORS.REGULATION_REQUIRED',
            ctaLink: smsConfigLink,
          },
        ],
        [
          ValidationErrorCode.VALIDATION_ERROR_CODE_REGULATION_IN_PROGRESS,
          {
            translationKey: 'VALIDATION.SENDER_VALIDATION.ERRORS.REGULATION_IN_PROGRESS',
            ctaLink: smsConfigLink,
          },
        ],
        [
          ValidationErrorCode.VALIDATION_ERROR_CODE_REGULATION_REJECTED,
          {
            translationKey: 'VALIDATION.SENDER_VALIDATION.ERRORS.REGULATION_REJECTED',
            ctaLink: smsConfigLink,
          },
        ],
        [
          ValidationErrorCode.VALIDATION_ERROR_CODE_SENDER_MISSING_PHYSICAL_ADDRESS,
          {
            translationKey: 'VALIDATION.SENDER_VALIDATION.ERRORS.SENDER_MISSING_PHYSICAL_ADDRESS',
            ctaLink: `account/location/${accountGroupID}/administration/profile`,
          },
        ],
        [
          ValidationErrorCode.VALIDATION_ERROR_CODE_PROFILING_CONSENT_WARNING,
          {
            translationKey: 'VALIDATION.SENDER_VALIDATION.ERRORS.PROFILING_CONSENT_WARNING',
          },
        ],
      ]);
      return config;
    }),
  );
}
